import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import { reduxForm, Field, propTypes, change, untouch } from 'redux-form';
import newAccountValidation from './validation/newAccountValidation';
import ValidationInput from './common/ValidationInput';
import ValidationSelect from './common/ValidationSelect';
import ValidationTextarea from './common/ValidationTextarea';
import HiddenInput from './common/HiddenInput';
import { NEW_ACCOUNT_FORM } from '../redux/modules/form';
import { stateFromAbbreviation, STATES } from '../constants/general';
import { ACCOUNT_TYPE_OPTIONS, DEPOSIT_OPTIONS } from '../constants/payment';
import { objectPropHasChanged } from '../utils/object';
import { newAccountFormTestDataIfRequested } from '../utils/testData';

const ACCOUNT_TYPE_OPTIONS_VALUES = _.values(ACCOUNT_TYPE_OPTIONS);
const DEPOSIT_OPTIONS_VALUES = _.values(DEPOSIT_OPTIONS);
const STATES_VALUES = _.values(STATES);

const getExtraBankInformation = (bankDetails) => {
  if (!_.isEmpty(bankDetails)) {
    const name = _.get(bankDetails, 'name', '');
    return name;
  }
};

/* eslint-disable jsx-a11y/label-has-for */
export function NewAccountFields({
  title,
  bankDetails,
  bankNameValidate,
  onChangeRoutingNumber,
  onZipCodeChange,
}) {
  const extraBankInformation = getExtraBankInformation(bankDetails);
  return (
    <div>
      <legend>{title}</legend>
      <Field
        name="companyName"
        type="text"
        component={ValidationInput}
        label="Dealership's Name"
        placeholder="eg. Pepmore Auto Sales"
      />
      <Field
        name="zipCode"
        type="text"
        component={ValidationInput}
        label="Zip Code"
        placeholder="eg. 11101"
        onChange={onZipCodeChange}
      />
      <Field
        name="address"
        type="text"
        component={ValidationInput}
        label="Address"
        placeholder="eg. 917 Asbury"
      />
      <Field
        name="city"
        type="text"
        component={ValidationInput}
        label="City"
        placeholder="eg. St. George"
      />
      <Field
        name="state"
        type="text"
        component={ValidationSelect}
        label="State"
        options={STATES_VALUES}
      />
      <Field
        name="routingNumber"
        type="text"
        component={ValidationInput}
        label="Routing number"
        placeholder="eg. 490000018"
        onChange={onChangeRoutingNumber}
      />
      <div className={cn('col-sm-offset-2')}>
        <label>{extraBankInformation}</label>
      </div>
      <Field
        name="accountNumber"
        type="text"
        component={ValidationInput}
        label="Account number"
        placeholder="eg. 24413325"
      />
      <Field
        name="accountType"
        component={ValidationSelect}
        label="Account type"
        options={ACCOUNT_TYPE_OPTIONS_VALUES}
      />
      <Field
        name="bankName"
        type="text"
        component={ValidationInput}
        label="Bank Name"
        placeholder="eg. Bank of Ashbury"
        validate={bankNameValidate ? [bankNameValidate] : undefined}
      />
    </div>
  );
} /* eslint-enable jsx-a11y/label-has-for */

NewAccountFields.propTypes = {
  title: PropTypes.string,
  bankDetails: PropTypes.object,
  bankNameValidate: PropTypes.func,
  onChangeRoutingNumber: PropTypes.func,
  onZipCodeChange: PropTypes.func,
};

NewAccountFields.defaultProps = {
  title: 'Billing Details',
  bankDetails: null,
  bankNameValidate: _.noop,
  onChangeRoutingNumber: _.noop,
  onZipCodeChange: _.noop,
};

export function NewPaymentFields({ title, disableAmount = false }) {
  return (
    <div>
      <legend>{title}</legend>
      <Field
        name="submittedByName"
        type="text"
        component={ValidationInput}
        label="Submitted by"
        placeholder="eg. George Clinton Jr."
      />
      <Field
        name="submittedByEmail"
        type="text"
        component={ValidationInput}
        label="Submitted by email"
        placeholder="eg. george.clinton.jr@gmail.com"
      />
      <Field
        name="amount"
        type="text"
        component={ValidationInput}
        label="Amount"
        placeholder="eg. 350.00"
        disabled={disableAmount}
      />
      <Field
        name="checkNumber"
        type="text"
        component={ValidationInput}
        label="Check No. (Optional)"
        placeholder="eg. 4681"
      />
      <Field
        name="depositType"
        component={ValidationSelect}
        label="Deposit type"
        options={DEPOSIT_OPTIONS_VALUES}
        disabled={disableAmount}
      />
      <Field
        name="memo"
        component={ValidationTextarea}
        label="Comments (memo)"
        placeholder="eg. New contracts printed up for September seasonal sale."
      />
      <Field name="contractNumbers" component={HiddenInput} />
      <Field name="amounts" component={HiddenInput} />
    </div>
  );
}

NewPaymentFields.propTypes = {
  title: PropTypes.string,
  disableAmount: PropTypes.bool,
};

NewPaymentFields.defaultProps = {
  title: 'Payment Details',
  disableAmount: false,
};

export class NewAccountForm extends Component {
  static propTypes = {
    ...propTypes,
    onChangeRoutingNumber: PropTypes.func.isRequired,
    onZipCodeChange: PropTypes.func.isRequired,
    onChangeDealerId: PropTypes.func.isRequired,
    bankDetails: PropTypes.object,
    dealerInfo: PropTypes.object,
    disableSubmit: PropTypes.bool,
    zipCode: PropTypes.array,
  };

  static defaultProps = {
    bankDetails: null,
    dealerInfo: null,
    disableSubmit: false,
    zipCode: null,
  };

  componentWillReceiveProps = (nextProps) => {
    const { dispatch, dealerInfo, bankDetails } = this.props;
    // Auto-fill dealer info from user account
    if (objectPropHasChanged(dealerInfo, nextProps.dealerInfo)) {
      dispatch(change(NEW_ACCOUNT_FORM, 'companyName', _.get(nextProps.dealerInfo, 'dealerName')));
      dispatch(change(NEW_ACCOUNT_FORM, 'address', _.get(nextProps.dealerInfo, 'dealerAddress')));
      dispatch(change(NEW_ACCOUNT_FORM, 'city', _.get(nextProps.dealerInfo, 'dealerCity')));
      dispatch(
        change(
          NEW_ACCOUNT_FORM,
          'state',
          stateFromAbbreviation(_.get(nextProps.dealerInfo, 'dealerState')),
        ),
      );
      dispatch(change(NEW_ACCOUNT_FORM, 'zipCode', _.get(nextProps.dealerInfo, 'dealerZip')));
      dispatch(change(NEW_ACCOUNT_FORM, 'accountType', ACCOUNT_TYPE_OPTIONS.CHECKING));
      dispatch(change(NEW_ACCOUNT_FORM, 'depositType', DEPOSIT_OPTIONS.STATEMENT));
    }

    // Auto-fill bank details
    if (objectPropHasChanged(bankDetails, nextProps.bankDetails)) {
      dispatch(change(NEW_ACCOUNT_FORM, 'bankName', _.get(nextProps.bankDetails, 'name')));
      dispatch(untouch(NEW_ACCOUNT_FORM, 'bankName'));
    }
    if (objectPropHasChanged(this.props.zipCode, nextProps.zipCode)) {
      dispatch(change(NEW_ACCOUNT_FORM, 'city', _.get(nextProps.zipCode[0], 'city')));
      dispatch(
        change(
          NEW_ACCOUNT_FORM,
          'state',
          stateFromAbbreviation(_.get(nextProps.zipCode[0], 'state')),
        ),
      );
    }
  };

  handleSubmitAndReset = (data) => {
    const { handleSubmit } = this.props;
    handleSubmit(data);
  };

  render() {
    const {
      error,
      user,
      bankDetails,
      disableSubmit,
      onChangeRoutingNumber,
      onChangeDealerId,
      onZipCodeChange,
    } = this.props;

    return (
      <form className="form-horizontal" onSubmit={this.handleSubmitAndReset}>
        {!user && (
          <Field
            name="dealerId"
            type="text"
            component={ValidationInput}
            label="Dealer ID (4 Digit)"
            placeholder="eg. 5713"
            onChange={onChangeDealerId}
          />
        )}
        <NewAccountFields
          bankDetails={bankDetails}
          onChangeRoutingNumber={onChangeRoutingNumber}
          onZipCodeChange={onZipCodeChange}
        />
        <NewPaymentFields />
        {error && (
          <p className="text-danger">
            <strong>{error}</strong>
          </p>
        )}
        <div className="row">
          <div className="col-md-3 col-xs-12">
            <button className="btn btn-primary btn-block" type="submit" disabled={disableSubmit}>
              <i className="fa fa-money" /> Pay to PRco
            </button>
          </div>
        </div>
      </form>
    );
  }
}

export default reduxForm({
  form: NEW_ACCOUNT_FORM,
  validate: newAccountValidation,
  initialValues: newAccountFormTestDataIfRequested(),
})(NewAccountForm);
